import {
  EventingHelpers,
  useLocalEventing
} from '@app/common/src/hooks/useLocalEventing/useLocalEventing'
import { IIdleTimer, useIdleTimer } from 'react-idle-timer'
import { differenceInSeconds, isValid } from 'date-fns'
import { useAuthState } from '@dtx-company/inter-app/src/hooks/useAuthState'

export interface ChangeTimestampEventProps {
  event?: Event
  prevTimestamp: Date
  timestamp: Date
}

export interface InitTimestampEventProps {
  timestamp: Date
}

const TIME_LAST_ACTIVE_KEY = 'timeLastActive'

type IdleTimerEvent = (props: { event?: Event }) => void
type TimestampInitEvent = (props: InitTimestampEventProps) => void
type TimestampChangeEvent = (props: ChangeTimestampEventProps) => void

export type TimestampEvents = {
  onTimestampInit: TimestampInitEvent
  onTimestampChange: TimestampChangeEvent
  onAction: IdleTimerEvent
  onActive: IdleTimerEvent
  onPrompt: IdleTimerEvent
  onIdle: IdleTimerEvent
}

export interface TimeStampTrackingHelpers {
  getLastActiveTime: IIdleTimer['getLastActiveTime']
  addEventHandler: EventingHelpers<TimestampEvents>['addEventHandler']
  removeEventHandler: EventingHelpers<TimestampEvents>['removeEventHandler']
}

export function useTimestampTracking(): TimeStampTrackingHelpers {
  const { addEventHandler, removeEventHandler, triggerEvent } = useLocalEventing<TimestampEvents>()

  const { isAuthenticated, jwt } = useAuthState()

  const localStorageItemKey = `${TIME_LAST_ACTIVE_KEY}-${jwt?.ithacaId}`

  const initTimestamp = (timestamp: Date): void => {
    if (!isAuthenticated) return

    localStorage.setItem(localStorageItemKey, timestamp.toISOString())

    triggerEvent('onTimestampInit', { timestamp })
  }
  interface TimestampChangeDates {
    prevTimestamp?: Date
    timestamp: Date
  }

  const getTimestamp = (): TimestampChangeDates => {
    const prevTimestamp = localStorage.getItem(localStorageItemKey)
    const timestamp = new Date()

    if (typeof prevTimestamp !== 'string') {
      initTimestamp(timestamp)
      return { timestamp }
    } else {
      const prevTimestampDate = new Date(prevTimestamp)
      if (!isValid(prevTimestampDate)) initTimestamp(timestamp)

      return {
        prevTimestamp: prevTimestampDate,
        timestamp
      }
    }
  }

  const shouldChangeTimestamp = ({ prevTimestamp, timestamp }: TimestampChangeDates): boolean => {
    if (prevTimestamp && differenceInSeconds(timestamp, prevTimestamp) > 0) return true
    else return false
  }

  const changeTimestamp = (props: ChangeTimestampEventProps): void => {
    if (!isAuthenticated) return

    localStorage.setItem(localStorageItemKey, props.timestamp.toISOString())

    triggerEvent('onTimestampChange', props)
  }

  const onActive = (event?: Event): void => {
    triggerEvent('onActive', { event })

    const { prevTimestamp, timestamp } = getTimestamp()

    shouldChangeTimestamp({ prevTimestamp, timestamp }) &&
      changeTimestamp({ event, prevTimestamp: prevTimestamp as Date, timestamp })
  }

  const onAction = (event?: Event): void => {
    triggerEvent('onAction', { event })

    const { prevTimestamp, timestamp } = getTimestamp()

    shouldChangeTimestamp({ prevTimestamp, timestamp }) &&
      changeTimestamp({ event, prevTimestamp: prevTimestamp as Date, timestamp })
  }

  const { getLastActiveTime } = useIdleTimer({
    onActive,
    onAction,
    onPrompt: (event?: Event): void => triggerEvent('onPrompt', { event }),
    onIdle: (event?: Event): void => triggerEvent('onIdle', { event })
  })

  return {
    getLastActiveTime,
    addEventHandler,
    removeEventHandler
  }
}
